import React from 'react';
import { useFrame } from '@react-three/fiber';
import { Select } from '@react-three/postprocessing';
import { useVisualizationStore } from '../../stores';
import { SceneObject } from '../../types';
import { DeviceObject } from './objects/DeviceObject';
import { PrimitiveObject } from './objects/PrimitiveObject';
import { ModelObject } from './objects/ModelObject';
import { TransformControls } from './controls/TransformControls';
import { SelectionBox } from './overlays/SelectionBox';

export const SceneObjects: React.FC = () => {
  const {
    scene,
    selectedObjects,
    transformMode,
    isTransforming,
    mockData,
    visualizationMode,
    showDataLabels,
    showBoundingBoxes,
  } = useVisualizationStore();

  // Update mock data animation frame
  useFrame(() => {
    // This could be used for real-time data updates
    // For now, mock data updates are handled in the store
  });

  // Early return if no scene is loaded
  if (!scene?.objects) {
    return null;
  }

  const renderObject = (obj: SceneObject) => {
    const isSelected = selectedObjects.includes(obj.id);
    const deviceData = obj.type === 'device' ? { sensors: mockData.sensors, heatmap: mockData.heatmap } : undefined;

    const commonProps = {
      key: obj.id,
      object: obj,
      isSelected,
      deviceData,
      visualizationMode,
      showDataLabels,
      showHeatmap: showBoundingBoxes,
    };

    switch (obj.type) {
      case 'device':
        return <DeviceObject {...commonProps} />;
      case 'primitive':
        return <PrimitiveObject {...commonProps} />;
      case 'model':
        return <ModelObject {...commonProps} />;
      default:
        return null;
    }
  };

  return (
    <>
      {/* Render all scene objects */}
      {scene.objects.map(renderObject)}

      {/* Selection outline for post-processing */}
      <Select enabled={selectedObjects.length > 0}>
        {scene.objects.filter((obj: SceneObject) => selectedObjects.includes(obj.id)).map(renderObject)}
      </Select>

      {/* Transform controls for selected objects */}
      {selectedObjects.length > 0 && (
        <TransformControls
          objects={scene.objects.filter((obj: SceneObject) => selectedObjects.includes(obj.id))}
          mode={transformMode}
          enabled={!isTransforming}
        />
      )}

      {/* Selection box for multi-select */}
      <SelectionBox />
    </>
  );
};
